import React, { Component } from 'react';
import { Box, Button, Flex, FlexColumn, Grid, Heading, Text } from 'core/components';
import { defaultValueRenderer } from 'core/components/select/Select';
import { Field, formConsumer, Select } from 'core/form';
import CheckCard from 'app/views/settings/ai/CheckCard';
import { withTheme } from 'styled-components';
import { inject, observer } from 'mobx-react';
import { isEmpty } from 'lodash';
import CreateAiCredentialDialog from 'app/views/settings/ai/CreateAiCredentialDialog';
import { FieldLabel } from 'core/form/components/FieldLabel';

const AI_FIELDS = {
  provider: 'provider',
  model: 'model',
  region: 'region',
  credential: 'credential',
  keyTypes: 'keyTypes'
};

@withTheme
@inject('$credentials', '$ai', '$auth')
@formConsumer
@observer
class ModelSettings extends Component {
  state = {
    modelOptions: [],
    regionOptions: [],
    credentialOptions: [],
    newCredentialModel: null,
    useKentikAccess: true
  };

  componentDidMount() {
    const { $credentials } = this.props;

    this.setState({ useKentikAccess: !this.getValue(AI_FIELDS.credential) });

    $credentials.groupedCollection.fetch().then(() => {
      this.setOptions([AI_FIELDS.model, AI_FIELDS.region, AI_FIELDS.credential]);
      if (this.isDefaultProviderSelected) {
        this.resetValues([AI_FIELDS.model, AI_FIELDS.region]);
      }
    });
  }

  getFieldName = (name) => {
    const { tier, $ai } = this.props;
    return `${$ai.getTierSettingsKey(tier)}.${name}`;
  };

  getField = (name) => {
    const { form } = this.props;
    return form.getField(this.getFieldName(name));
  };

  getValue = (name) => this.getField(name)?.value;

  // the company's saved model, if it belongs to `provider`
  savedModelFor = (provider) =>
    this.getField(AI_FIELDS.provider)?.initialValue === provider
      ? this.getField(AI_FIELDS.model)?.initialValue
      : undefined;

  get isDefaultProviderSelected() {
    const { $ai } = this.props;
    return $ai.isDefaultProvider(this.getValue(AI_FIELDS.provider));
  }

  getFieldProps = (fieldName) => {
    const { $auth } = this.props;
    const { state } = this;
    const isAdminUser = $auth.isPreciselyAdministrator;

    // Fields that should be disabled when default provider is selected
    const disabledWhenDefault = [AI_FIELDS.model, AI_FIELDS.region];

    const shouldDisable = disabledWhenDefault.includes(fieldName)
      ? isAdminUser || this.isDefaultProviderSelected
      : isAdminUser;

    const props = {
      name: this.getFieldName(fieldName),
      large: true,
      showLabel: true,
      disabled: shouldDisable
    };

    // Add options from state if available (e.g., modelOptions, regionOptions, credentialOptions)
    const optionsKey = `${fieldName}Options`;
    if (state[optionsKey]) {
      props.options = state[optionsKey];
    }

    return props;
  };

  setOptions = (fields = []) => {
    const { tier, $credentials, $ai } = this.props;
    const { useKentikAccess } = this.state;
    const { getModelOptions, getRegionOptions } = $ai;

    const validFields = [AI_FIELDS.model, AI_FIELDS.region, AI_FIELDS.credential];

    const provider = this.getValue(AI_FIELDS.provider);
    const model = this.getValue(AI_FIELDS.model);

    const allOptions = {};

    fields
      .filter((fieldName) => validFields.includes(fieldName))
      .forEach((fieldName) => {
        let fieldOptions = [];

        if (fieldName === AI_FIELDS.model) {
          fieldOptions = getModelOptions(tier, provider, this.savedModelFor(provider));
        } else if (fieldName === AI_FIELDS.region) {
          // note: it is assumed there is only one value
          const modelName = (this.isDefaultProviderSelected ? getModelOptions(tier, provider)[0]?.value : model) || '';
          fieldOptions = getRegionOptions(tier, provider, modelName);
          const regionField = this.getField(AI_FIELDS.region);
          regionField.setRules(fieldOptions.length ? 'required' : '');
        } else if (fieldName === AI_FIELDS.credential) {
          fieldOptions = !useKentikAccess ? $credentials.getCredentialOptionsByTypes(['ai_api_token']) : [];
        }

        allOptions[`${fieldName}Options`] = fieldOptions;
      });

    // update all at once
    if (!isEmpty(allOptions)) {
      this.setState({ ...allOptions });
    }
  };

  resetValues = (fields = []) => {
    const { useKentikAccess } = this.state;
    // note: provider is independent of any field's changes, so it won't be reset here
    const validFields = [AI_FIELDS.model, AI_FIELDS.region, AI_FIELDS.credential, AI_FIELDS.keyTypes];

    fields
      .filter((fieldName) => validFields.includes(fieldName))
      .forEach((fieldName) => {
        const field = this.getField(fieldName);

        if (this.isDefaultProviderSelected && (fieldName === AI_FIELDS.model || fieldName === AI_FIELDS.region)) {
          this.setState((state) => {
            // note: it is assumed there is only one value
            field.setValue(state[`${fieldName}Options`][0]?.value);
          });
        } else if (fieldName === AI_FIELDS.keyTypes) {
          field.setValue(!useKentikAccess ? 'api_key' : '');
        } else {
          if (fieldName === AI_FIELDS.credential) {
            field.setRules(!useKentikAccess ? 'required' : '');
          }
          field.setToDefault();
        }
      });
  };

  handleSelectAccessMethod = (value) => {
    this.setState({ useKentikAccess: value }, () => {
      this.resetValues([AI_FIELDS.credential, AI_FIELDS.keyTypes]);
      this.setOptions([AI_FIELDS.credential]);
    });
  };

  handleSelectProvider = (providerField, provider) => {
    const { $ai } = this.props;

    // don't reset if clicking on same provider again
    const currentProvider = this.getValue(AI_FIELDS.provider);
    if (currentProvider === provider) {
      return;
    }

    providerField?.setValue(provider);
    const onlyUseKentikAccess = $ai.isDefaultProvider(provider) || !$ai.isCredentialSupported(provider);

    if (onlyUseKentikAccess) {
      this.setState({ useKentikAccess: true }, () => {
        this.setOptions([AI_FIELDS.model, AI_FIELDS.region, AI_FIELDS.credential]);
        this.resetValues([AI_FIELDS.model, AI_FIELDS.region, AI_FIELDS.credential, AI_FIELDS.keyTypes]);
      });
    } else {
      this.setOptions([AI_FIELDS.model, AI_FIELDS.region, AI_FIELDS.credential]);
      this.resetValues([AI_FIELDS.model, AI_FIELDS.region, AI_FIELDS.credential, AI_FIELDS.keyTypes]);
    }
  };

  handleTestToken = async () => {
    const { $ai, $auth } = this.props;

    this.setState({ isTestingToken: true });

    const payload = {
      provider_model: this.getValue(AI_FIELDS.model),
      provider_region: this.getValue(AI_FIELDS.region) || '',
      provider_id: this.getValue(AI_FIELDS.provider),
      vault_secret_id: this.getValue(AI_FIELDS.credential),
      provider_key_types: this.getValue(AI_FIELDS.keyTypes),
      company_id: Number($auth.getActiveUserProperty('company_id'))
    };
    await $ai.testCredential(payload);

    this.setState({ isTestingToken: false });
  };

  // tagged model labels are full-width flex rows; the default renderer would wrap them in an inline span
  modelValueRenderer = (option, ...rest) =>
    option?.filterLabel ? option.label : defaultValueRenderer(option, ...rest);

  renderCredentialActions = () => {
    const { $credentials } = this.props;
    const { isTestingToken } = this.state;

    return (
      <Flex justifyContent="flex-start" alignItems="center" gap={2} mt={2}>
        <Button
          intent="primary"
          minimal
          width="fit-content"
          small
          icon="plus"
          text="Create AI API Token"
          onClick={() => this.setState({ newCredentialModel: $credentials.groupedCollection.forge() })}
        />
        <Button
          icon="key"
          minimal
          intent="primary"
          loading={isTestingToken}
          onClick={this.handleTestToken}
          disabled={
            !this.getValue(AI_FIELDS.model) ||
            !this.getValue(AI_FIELDS.provider) ||
            !this.getValue(AI_FIELDS.credential)
          }
          small
          text="Test AI API Token"
        />
      </Flex>
    );
  };

  render() {
    const { tier, theme, description, $ai, $auth } = this.props;
    const { modelOptions, regionOptions, credentialOptions, newCredentialModel, useKentikAccess } = this.state;

    const selectedProvider = this.getValue(AI_FIELDS.provider);
    const isAdminUser = $auth.isPreciselyAdministrator;

    const menuWidth = 280;
    const selectStyle = { border: theme.borders.thinLight, p: '11px', minWidth: menuWidth };

    return (
      <FlexColumn gap={3}>
        <Box>
          <Heading level={4}>{$ai.getTierLabel(tier)} Model</Heading>
          <Text muted>{description}</Text>
        </Box>
        <Field {...this.getFieldProps(AI_FIELDS.provider)}>
          {({ field, ...rest }) => {
            const fieldValue = field.getValue();
            return (
              <FlexColumn>
                <FieldLabel fontWeight="bold" mb="4px">
                  Provider
                </FieldLabel>
                <Grid
                  gridTemplateColumns="repeat(auto-fill, minmax(200px, 1fr))"
                  gridRowGap={2}
                  gridColumnGap={2}
                  gridAutoRows="1fr"
                >
                  {field.options.map(({ label, value: provider }) => (
                    <CheckCard
                      key={provider}
                      label={label}
                      active={fieldValue === provider}
                      description={$ai.isDefaultProvider(provider) ? $ai.defaultProviderName(tier) : ''}
                      onClick={() => this.handleSelectProvider(field, provider)}
                      {...rest}
                    />
                  ))}
                </Grid>
              </FlexColumn>
            );
          }}
        </Field>
        <Flex gap={2}>
          <Field
            {...this.getFieldProps(AI_FIELDS.model)}
            onChange={() => {
              this.resetValues([AI_FIELDS.region]);
              this.setOptions([AI_FIELDS.region]);
            }}
          >
            <Select
              showFilter={modelOptions.length > 4}
              minimal
              buttonStyle={selectStyle}
              menuWidth={menuWidth}
              valueRenderer={this.modelValueRenderer}
            />
          </Field>
          {regionOptions.length > 0 && (
            <Field {...this.getFieldProps(AI_FIELDS.region)}>
              <Select showFilter={regionOptions.length > 4} minimal buttonStyle={selectStyle} menuWidth={menuWidth} />
            </Field>
          )}
        </Flex>
        {!this.isDefaultProviderSelected && (
          <>
            <FlexColumn>
              <FieldLabel fontWeight="bold" mb="4px">
                Credentials
              </FieldLabel>
              <Grid
                gridTemplateColumns="repeat(auto-fill, minmax(200px, 1fr))"
                gridRowGap={2}
                gridColumnGap={2}
                gridAutoRows="1fr"
              >
                <CheckCard
                  label="Use Kentik Access"
                  active={useKentikAccess}
                  disabled={isAdminUser}
                  onClick={() => this.handleSelectAccessMethod(true)}
                />
                <CheckCard
                  label="Use my own token"
                  active={!useKentikAccess}
                  onClick={() => this.handleSelectAccessMethod(false)}
                  disabled={!$ai.isCredentialSupported(tier, selectedProvider) || isAdminUser}
                />
              </Grid>
            </FlexColumn>
            {!useKentikAccess && (
              <FlexColumn>
                <Field
                  {...this.getFieldProps(AI_FIELDS.credential)}
                  subLabel="Only AI API Token credentials are available for selection"
                >
                  <Select
                    showFilter={credentialOptions.length > 4}
                    minimal
                    buttonStyle={selectStyle}
                    menuWidth={menuWidth}
                  />
                </Field>
                {!isAdminUser && this.renderCredentialActions()}
                {newCredentialModel && (
                  <CreateAiCredentialDialog
                    model={newCredentialModel}
                    onClose={() => this.setState({ newCredentialModel: null })}
                    onSave={() => this.setOptions([AI_FIELDS.credential])}
                  />
                )}
              </FlexColumn>
            )}
          </>
        )}
      </FlexColumn>
    );
  }
}

export default ModelSettings;
